import {
  createContext,
  type FC,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

interface HeaderActionsContextValue {
  containerEl: HTMLDivElement | null;
  assignContainerEl: (element: HTMLDivElement | null) => void;
}

const HeaderActionsContext = createContext<HeaderActionsContextValue | null>(
  null
);

export const HeaderActionsProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);

  const assignContainerEl = useCallback((element: HTMLDivElement | null) => {
    setContainerEl(element);
  }, []);

  const headerActionsContextValue: HeaderActionsContextValue = useMemo(
    () => ({ assignContainerEl, containerEl }),
    [assignContainerEl, containerEl]
  );

  return (
    <HeaderActionsContext.Provider value={headerActionsContextValue}>
      {children}
    </HeaderActionsContext.Provider>
  );
};

/**
 * Ref callback for the header mount node (right slot in {@link AppLayout}).
 */
export const useHeaderActionsContainerRef = (): ((
  element: HTMLDivElement | null
) => void) => {
  const ctx = useContext(HeaderActionsContext);
  if (!ctx) {
    throw new Error(
      'useHeaderActionsContainerRef must be used within HeaderActionsProvider'
    );
  }
  return ctx.assignContainerEl;
};

/**
 * DOM container for React `createPortal` from a page.
 */
export const useHeaderActionsContainer = (): HTMLDivElement | null => {
  const ctx = useContext(HeaderActionsContext);
  if (!ctx) {
    throw new Error(
      'useHeaderActionsContainer must be used within HeaderActionsProvider'
    );
  }
  return ctx.containerEl;
};
